export type UnitSystem = 'imperial' | 'metric';

const KM_TO_MILES = 0.621371;
const KM_TO_FEET = 3280.84;

/**
 * Format a distance in kilometers to a human-readable string
 * using the specified unit system.
 */
export function formatDistance(km: number, units: UnitSystem): string {
  if (units === 'metric') {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km.toFixed(1)}km`;
  }

  // Imperial
  const miles = km * KM_TO_MILES;
  if (miles < 0.18) {
    const feet = km * KM_TO_FEET;
    return `${Math.round(feet)} ft`;
  }
  return `${miles.toFixed(2)} mi`;
}

/**
 * Calculate the distance between two coordinates using the haversine formula,
 * then format the result.
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  units: UnitSystem,
): string {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371; // Earth's radius in km

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const km = R * c;

  return formatDistance(km, units);
}
